/**
 * Marked items — consultant's per-deployment bookmark store.
 *
 * Provides a single context per <DeploymentDetail> so the count badge in
 * the header, the per-row Mark buttons, and the tray all share the same
 * authoritative list and refetch in sync.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "./api";

const Ctx = createContext({
  marks: [],
  loading: false,
  error: "",
  isMarked: () => null,
  create: async () => {},
  updateNote: async () => {},
  remove: async () => {},
  refresh: async () => {},
  open: () => {},
  isOpen: false,
  close: () => {},
});

/** Stable string match against the backend's _dedupe_key. */
export function dedupeKey(kind, payload) {
  if (kind === "item") return `item:${payload.item_number}`;
  if (kind === "dimension_pair") {
    const a = payload.segment_a;
    const b = payload.segment_b;
    return `dpair:${payload.dimension}:${a.key}=${a.value}|${b.key}=${b.value}`;
  }
  if (kind === "trend_delta") {
    return `trend:${payload.from_deployment_id}:${payload.dimension}`;
  }
  if (kind === "benchmark_placement") {
    return `bench:${payload.dimension}`;
  }
  return "";
}

export function MarkedItemsProvider({ deploymentId, children }) {
  const [marks, setMarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!deploymentId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/api/deployments/${deploymentId}/marked-items`);
      setMarks(data.marks || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [deploymentId]);

  // setState calls live inside refresh's async body; the IIFE keeps them
  // off the effect's synchronous frame (react-hooks/set-state-in-effect).
  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await refresh(); })();
    return () => { cancelled = true; };
  }, [refresh]);

  const isMarked = useCallback(
    (kind, payload) => {
      const key = dedupeKey(kind, payload);
      return marks.find((m) => m.dedupe_key === key) || null;
    },
    [marks],
  );

  const create = useCallback(async (kind, payload, consultantNote = "") => {
    const { data } = await api.post(
      `/api/deployments/${deploymentId}/marked-items`,
      { kind, payload, consultant_note: consultantNote },
    );
    setMarks((cur) => [...cur, data]);
    return data;
  }, [deploymentId]);

  const updateNote = useCallback(async (markedId, note) => {
    const { data } = await api.patch(
      `/api/deployments/${deploymentId}/marked-items/${markedId}`,
      { consultant_note: note },
    );
    setMarks((cur) => cur.map((m) => (m.id === markedId ? data : m)));
    return data;
  }, [deploymentId]);

  const remove = useCallback(async (markedId) => {
    await api.delete(`/api/deployments/${deploymentId}/marked-items/${markedId}`);
    setMarks((cur) => cur.filter((m) => m.id !== markedId));
  }, [deploymentId]);

  const value = useMemo(() => ({
    marks, loading, error,
    isMarked, create, updateNote, remove, refresh,
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  }), [marks, loading, error, isMarked, create, updateNote, remove, refresh, isOpen]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMarkedItems() {
  return useContext(Ctx);
}
